// The template picker (2026-10-08): the dialog the reply box's Templates
// button opens, extracted from SuggestionPanel.jsx so the New view uses the
// same one. Folders, Suggestions, search and the other-clients section all
// load from the server for one scope: { account, trqClientCode, team }, the
// same scoping the server applies (routes/templates.js). With no scope (the
// New view before a From is chosen) the dialog shows only noScopeMessage.
// Choosing a template calls onUse(template); inserting it is the caller's job.
import { useEffect, useState } from 'react'
import {
  Box, Stack, Typography, Button, TextField, Paper, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText,
} from '@mui/material'
import { fetchTemplateFolders, fetchTemplatesByFolder, fetchOtherClientTemplateFolders, searchTemplates, fetchTemplateSuggestions } from './api.js'
import { previewHtml, PREVIEW_PARAGRAPH_SX } from './templatePreview.js'
import { showOtherClientSection, otherClientSubfolderLabel, otherClientNote } from './otherClientTemplates.js'
import { splitOwnFolderGroups, groupedFolderLabel } from './templateFolderGroups.js'

export default function TemplatePickerDialog({ open, onClose, onUse, scope = null, intent = null, showOtherClients = true, noScopeMessage = '', disableRestoreFocus = false }) {
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateFolder, setTemplateFolder] = useState(null)
  const [templateFolders, setTemplateFolders] = useState([])
  // The trees shown as their own labelled group after the rest (2026-10-22:
  // a Website Support contact's "1A Support" / "AM Support").
  const [templateFolderGroups, setTemplateFolderGroups] = useState([])
  // The other-clients section (2026-10-08): the groups the server returned,
  // which group rows are expanded (none each time the dialog opens), and
  // whether the selected folder came from that section.
  const [otherClientGroups, setOtherClientGroups] = useState([])
  const [expandedOtherClients, setExpandedOtherClients] = useState(() => new Set())
  const [templateFolderIsOtherClient, setTemplateFolderIsOtherClient] = useState(false)
  const [templateList, setTemplateList] = useState([])
  const [templateSuggestions, setTemplateSuggestions] = useState([])
  const [templateLoading, setTemplateLoading] = useState(false)
  const [templatePreviewId, setTemplatePreviewId] = useState(null)
  const account = scope?.account ?? null
  const trqClientCode = scope?.trqClientCode ?? null
  const team = scope?.team ?? null
  const hasScope = Boolean(scope)
  const query = { account, trqClientCode, team }
  const ownFolders = splitOwnFolderGroups(templateFolders, templateFolderGroups)

  // Each opening starts fresh and loads the folders, the Suggestions for the
  // intent, and the other clients' folders for this scope.
  useEffect(() => {
    if (!open) return
    setTemplateSearch('')
    setTemplateFolder(null)
    setTemplateFolderIsOtherClient(false)
    setExpandedOtherClients(new Set())
    setOtherClientGroups([])
    setTemplateList([])
    setTemplateSuggestions([])
    setTemplatePreviewId(null)
    if (!hasScope) { setTemplateFolders([]); setTemplateFolderGroups([]); setTemplateLoading(false); return }
    let active = true
    setTemplateLoading(true)
    Promise.all([
      fetchTemplateFolders(query),
      intent ? fetchTemplateSuggestions({ intent, ...query }) : Promise.resolve([]),
      showOtherClients ? fetchOtherClientTemplateFolders(query) : Promise.resolve([]),
    ])
      .then(([folderListing, suggestions, otherGroups]) => {
        if (!active) return
        setTemplateFolders(folderListing?.folders ?? [])
        setTemplateFolderGroups(folderListing?.groups ?? [])
        setTemplateSuggestions(suggestions)
        setOtherClientGroups(otherGroups)
      })
      .catch(() => { /* non-fatal — dialog is still usable */ })
      .finally(() => { if (active) setTemplateLoading(false) })
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasScope, account, trqClientCode, team, intent, showOtherClients])

  // Load templates when a folder is selected
  useEffect(() => {
    if (!open || !hasScope || !templateFolder) return
    setTemplateLoading(true)
    fetchTemplatesByFolder(templateFolder, { ...query, scope: templateFolderIsOtherClient ? 'other-client' : undefined })
      .then(setTemplateList)
      .catch(() => setTemplateList([]))
      .finally(() => setTemplateLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateFolder, templateFolderIsOtherClient, account, trqClientCode, team])

  // Search templates as the user types (debounced via character threshold)
  useEffect(() => {
    if (!open || !hasScope) return
    if (templateSearch.trim().length < 2) {
      if (!templateFolder) setTemplateList([])
      return
    }
    setTemplateLoading(true)
    searchTemplates(templateSearch.trim(), query)
      .then(setTemplateList)
      .catch(() => setTemplateList([]))
      .finally(() => setTemplateLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateSearch, open, templateFolder, account, trqClientCode, team])

  function selectOwnTemplateFolder(folder) {
    setTemplateFolderIsOtherClient(false)
    setTemplateFolder(folder)
  }
  function selectOtherClientTemplateFolder(folder) {
    setTemplateFolderIsOtherClient(true)
    setTemplateFolder(folder)
  }
  function toggleOtherClientGroup(prefix) {
    setExpandedOtherClients((prev) => {
      const next = new Set(prev)
      if (next.has(prefix)) next.delete(prefix); else next.add(prefix)
      return next
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableRestoreFocus={disableRestoreFocus}>
      <DialogTitle>Select Template</DialogTitle>
      {!scope ? (
      <DialogContent sx={{ minHeight: 120 }}>
        <Typography color="text.secondary" variant="body2" data-testid="template-dialog-no-scope" sx={{ p: 1.5 }}>
          {noScopeMessage}
        </Typography>
      </DialogContent>
      ) : (
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 480 }}>
        {/* Search bar */}
        <Box sx={{ p: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search templates…"
            value={templateSearch}
            onChange={(e) => { setTemplateSearch(e.target.value); selectOwnTemplateFolder(null) }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left: folder list (only when not searching) */}
          {!templateSearch.trim() && (
            <Box sx={{ width: 220, borderRight: 1, borderColor: 'divider', overflowY: 'auto', flexShrink: 0 }}>
              <List dense disablePadding>
                <ListItemButton
                  selected={templateFolder === null}
                  onClick={() => selectOwnTemplateFolder(null)}
                  sx={{ px: 1.5, py: 0.5 }}
                >
                  <ListItemText primary="Suggestions" primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }} />
                </ListItemButton>
                {ownFolders.main.map((f) => (
                  <ListItemButton
                    key={f}
                    selected={!templateFolderIsOtherClient && templateFolder === f}
                    onClick={() => selectOwnTemplateFolder(f)}
                    sx={{ px: 1.5, py: 0.5 }}
                  >
                    <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2', noWrap: true }} sx={{ flex: 1, minWidth: 0 }} />
                  </ListItemButton>
                ))}
                {/* A Website Support contact's Support tree (2026-10-22): its
                    own labelled group after the Website library, fully this
                    contact's own templates (not the other-clients section). */}
                {ownFolders.groups.map((g) => (
                  <Box component="li" key={g.prefix} sx={{ listStyle: 'none' }} data-testid="own-folder-group">
                    <Typography variant="body2" fontWeight={600} data-testid="own-folder-group-label" sx={{ px: 1.5, pt: 1, pb: 0.25, borderTop: 1, borderColor: 'divider', mt: 0.5 }}>
                      {g.label}
                    </Typography>
                    <List dense disablePadding>
                      {g.folders.map((f) => (
                        <ListItemButton
                          key={f}
                          selected={!templateFolderIsOtherClient && templateFolder === f}
                          onClick={() => selectOwnTemplateFolder(f)}
                          data-testid="own-folder-group-folder"
                          sx={{ pl: 3.5, pr: 1.5, py: 0.5 }}
                        >
                          <ListItemText primary={groupedFolderLabel(g, f)} primaryTypographyProps={{ variant: 'body2', noWrap: true }} sx={{ flex: 1, minWidth: 0 }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                ))}
                {/* The same company's other clients (2026-10-08): below a
                    bold divider, each client one collapsed row, grayed but
                    fully usable. Hidden while searching, like own folders. */}
                {showOtherClients && showOtherClientSection(otherClientGroups) && (
                  <>
                    <Box component="li" role="separator" data-testid="other-client-divider" sx={{ listStyle: 'none', borderTop: '2px solid', borderColor: 'text.secondary', my: 1, mx: 1 }} />
                    {otherClientGroups.map((g) => {
                      const open = expandedOtherClients.has(g.prefix)
                      return (
                        <Box component="li" key={g.prefix} sx={{ listStyle: 'none' }}>
                          <ListItemButton
                            onClick={() => toggleOtherClientGroup(g.prefix)}
                            aria-expanded={open}
                            data-testid="other-client-group"
                            sx={{ px: 1.5, py: 0.5 }}
                          >
                            <ListItemText primary={`${open ? '▾' : '▸'} ${g.label}`} primaryTypographyProps={{ variant: 'body2', noWrap: true, color: 'text.disabled', fontWeight: 400 }} />
                          </ListItemButton>
                          {open && (
                            <List dense disablePadding>
                              {g.folders.map((f) => (
                                <ListItemButton
                                  key={f}
                                  selected={templateFolderIsOtherClient && templateFolder === f}
                                  onClick={() => selectOtherClientTemplateFolder(f)}
                                  data-testid="other-client-folder"
                                  sx={{ pl: 3.5, pr: 1.5, py: 0.5 }}
                                >
                                  <ListItemText primary={otherClientSubfolderLabel(g, f)} primaryTypographyProps={{ variant: 'body2', noWrap: true, color: 'text.disabled', fontWeight: 400 }} sx={{ flex: 1, minWidth: 0 }} />
                                </ListItemButton>
                              ))}
                            </List>
                          )}
                        </Box>
                      )
                    })}
                  </>
                )}
              </List>
            </Box>
          )}

          {/* Right: template list */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {templateLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
            ) : (() => {
              const items = templateSearch.trim()
                ? templateList
                : templateFolder
                  ? templateList
                  : templateSuggestions

              if (items.length === 0) {
                return (
                  <Typography color="text.secondary" variant="body2" sx={{ p: 1.5 }}>
                    {templateSearch.trim()
                      ? 'No templates match your search.'
                      : templateFolder
                        ? 'No templates in this folder.'
                        : 'No intent-based suggestions. Select a folder or search.'}
                  </Typography>
                )
              }

              const crossClientNote = !templateSearch.trim() && templateFolderIsOtherClient ? otherClientNote(otherClientGroups, templateFolder) : null
              return (
                <Stack spacing={0.5}>
                  {crossClientNote && (
                    <Typography variant="caption" color="text.secondary" data-testid="other-client-note" sx={{ px: 0.5, pb: 0.5 }}>
                      {crossClientNote}
                    </Typography>
                  )}
                  {items.map((t) => (
                    <Paper key={t.id} variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setTemplatePreviewId(templatePreviewId === t.id ? null : t.id)}
                          >
                            {t.title}
                          </Typography>
                          {t.keywords && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t.keywords}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} flexShrink={0}>
                          <Button
                            size="small"
                            variant="text"
                            sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0 }}
                            onClick={() => setTemplatePreviewId(templatePreviewId === t.id ? null : t.id)}
                          >
                            {templatePreviewId === t.id ? 'Hide' : 'Preview'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0 }}
                            onClick={() => onUse(t)}
                          >
                            Use
                          </Button>
                        </Stack>
                      </Stack>
                      {templatePreviewId === t.id && (
                        <Box data-testid="template-preview" sx={{ px: 1.5, pb: 1, pt: 0, borderTop: 1, borderColor: 'divider', fontSize: '0.8rem', maxHeight: 200, overflowY: 'auto', ...PREVIEW_PARAGRAPH_SX }}>
                          <div dangerouslySetInnerHTML={{ __html: previewHtml(t.content) }} />
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )
            })()}
          </Box>
        </Box>
      </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
